import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronRight, Plus, Ticket } from 'lucide-react'
import type { DeskStat, RedeemAppStatus, RedeemAppWithStats, RedeemPostCounts, ScheduleItem } from '@/api/client'
import { useDeskStats, useRedeemApps, useSchedule, useTargets } from '@/lib/queries'
import { STATUS } from '@/lib/status'
import { formatShort } from '@/lib/time'
import { cn } from '@/lib/utils'
import { Button, EmptyState, ErrorState, PageHeader, Segmented, Skeleton, StatTile, useQueryParam, useWidePage } from '@/ui'
import { channelLabel } from '../schedule/shared'
import { AppCard, NO_POSTS, ready } from './AppCard'
import { NewAppDialog } from './shared'

type StatusFilter = 'all' | RedeemAppStatus
const FILTERS: StatusFilter[] = ['all', 'active', 'paused', 'archived']

/** How far back the cards list posts that went out. */
const POST_DAYS = 30

const isRunning = (r: RedeemAppWithStats) => r.app.status === 'active' && !r.app.ended_at

/** Running apps first, then the ones whose giveaway ended, paused, archived. */
const rank = (r: RedeemAppWithStats) => (isRunning(r) ? 0 : r.app.status === 'active' ? 1 : r.app.status === 'paused' ? 2 : 3)

/** /redeem: a card per app — its pool, today at the desk, and its promo posts
 *  with where and when each goes — over the running apps' totals. The table
 *  it replaced listed the numbers but not which posts, on which channel, were
 *  doing the work. */
export default function RedeemList() {
  useWidePage()
  const apps = useRedeemApps()
  const targets = useTargets().data ?? []
  const desk = useDeskStats(7)
  const since = useMemo(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() - POST_DAYS).toISOString()
  }, [])
  const feed = useSchedule({ source: 'redeem', from: since, includeUnscheduled: true }, { live: true })
  const [creating, setCreating] = useState(false)
  const [archivedShown, setArchivedShown] = useState(false)
  const [statusParam, setStatusParam] = useQueryParam('status', 'all')
  const status: StatusFilter = (FILTERS as string[]).includes(statusParam) ? (statusParam as StatusFilter) : 'all'

  const items = apps.data?.items ?? []
  const allApps = items.map((i) => i.app)
  const deskByApp = new Map<number, DeskStat>((desk.data?.items ?? []).map((s) => [s.app_id, s]))
  const postsByApp = new Map<number, ScheduleItem[]>()
  for (const it of feed.data?.items ?? []) {
    if (!it.app) continue
    postsByApp.set(it.app.id, [...(postsByApp.get(it.app.id) ?? []), it])
  }

  const shown = (status === 'all' ? items : items.filter((r) => r.app.status === status)).slice().sort((a, b) => rank(a) - rank(b))
  // Under 전체 the archived apps fold away: they are kept for their history,
  // not run.
  const live = status === 'all' ? shown.filter((r) => r.app.status !== 'archived') : shown
  const archived = status === 'all' ? shown.filter((r) => r.app.status === 'archived') : []
  const countOf = (f: StatusFilter) => (f === 'all' ? items.length : items.filter((r) => r.app.status === f).length)

  // The tiles add up the running apps: a paused or archived app's drafts are
  // not about to go anywhere.
  const running = items.filter(isRunning)
  const runningIds = new Set(running.map((r) => r.app.id))
  const sum = running.reduce<RedeemPostCounts>((a, r) => {
    const p = r.posts ?? NO_POSTS
    return { draft: a.draft + p.draft, scheduled: a.scheduled + p.scheduled, published: a.published + p.published, failed: a.failed + p.failed }
  }, NO_POSTS)
  const now = Date.now()
  const next = (feed.data?.items ?? [])
    .filter((it) => it.app && runningIds.has(it.app.id) && it.status === 'scheduled' && it.scheduled_at && Date.parse(it.scheduled_at) > now)
    .sort((a, b) => String(a.scheduled_at).localeCompare(String(b.scheduled_at)))[0]
  const nextAt = next?.scheduled_at ?? running.map((r) => r.posts?.next_at).filter((t): t is string => !!t).sort()[0]
  const empty = running.filter((r) => ready(r.posts ?? NO_POSTS) === 0)
  const today = (desk.data?.items ?? []).reduce(
    (a, s) => ({
      issued: a.issued + s.issued_today,
      requests: a.requests + s.requests_today,
      pending: a.pending + s.pending_new,
      failed: a.failed + s.failed_open,
    }),
    { issued: 0, requests: 0, pending: 0, failed: 0 },
  )

  const card = (row: RedeemAppWithStats) => (
    <AppCard
      key={row.app.id}
      row={row}
      apps={allApps}
      targets={targets}
      desk={deskByApp.get(row.app.id)}
      posts={postsByApp.get(row.app.id) ?? []}
      postsLoading={feed.isPending}
    />
  )

  return (
    <>
      <PageHeader
        title="리딤"
        description="앱마다 코드 풀을 두고, 요청이 오면 한 장씩 내보낸다."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
            새 앱
          </Button>
        }
      />
      <div className="space-y-5">
        {items.length > 0 && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              label="준비된 프로모션 글"
              value={ready(sum)}
              hint={`예약 ${sum.scheduled} · 초안 ${sum.draft} · 게시 ${sum.published}`}
              tone={ready(sum) === 0 && running.length > 0 ? 'publishing' : undefined}
            />
            <StatTile
              label="다음 게시"
              value={nextAt ? formatShort(nextAt) : '—'}
              hint={next?.app ? `${next.app.name} · ${channelLabel(next)}` : nextAt ? '예약된 글 중 가장 이른 것' : '예약된 글 없음'}
            />
            <StatTile
              label="오늘 발송"
              value={today.issued}
              hint={
                <>
                  요청 {today.requests} · 대기 {today.pending}
                  {today.failed > 0 && <span className="text-status-failed"> · 실패 {today.failed}</span>}
                </>
              }
              tone={today.failed > 0 ? 'failed' : undefined}
              to="/redeem/requests"
            />
            <StatTile
              label="준비된 글 없는 앱"
              value={empty.length}
              hint={empty.length > 0 ? empty.map((r) => r.app.name).join(', ') : `가동 ${running.length}개 모두 글이 있다`}
              tone={empty.length > 0 ? 'publishing' : undefined}
            />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Segmented<StatusFilter>
            label="앱 상태"
            value={status}
            onChange={(v) => setStatusParam(v === 'all' ? '' : v)}
            options={FILTERS.map((f) => ({ value: f, label: f === 'all' ? '전체' : STATUS.app[f].label, count: countOf(f) }))}
          />
          <Link to="/redeem/desk" className="inline-flex items-center gap-1 text-meta font-medium text-accent hover:underline">
            일별 발송 그래프 · 데스크 상태
            <ArrowRight size={12} aria-hidden />
          </Link>
        </div>

        {apps.error && !apps.data ? (
          <ErrorState title="리딤 앱을 불러오지 못했다" error={apps.error} onRetry={() => apps.refetch()} />
        ) : apps.isPending ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-80" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Ticket}
            title="앱이 없다"
            description="앱을 만든 뒤 App Store Connect에서 받은 코드 CSV를 올린다."
            actions={
              <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                새 앱
              </Button>
            }
          />
        ) : shown.length === 0 ? (
          <EmptyState icon={Ticket} title={`${STATUS.app[status]?.label ?? ''} 상태인 앱이 없다`} description="위에서 다른 상태를 고른다." />
        ) : (
          <>
            {live.length > 0 && (
              <div role="list" aria-label="리딤 앱" className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {live.map((r) => (
                  <div key={r.app.id} role="listitem" className="flex min-w-0">
                    {card(r)}
                  </div>
                ))}
              </div>
            )}
            {archived.length > 0 && (
              <section aria-label="보관된 앱" className="space-y-3">
                <button
                  type="button"
                  onClick={() => setArchivedShown((v) => !v)}
                  aria-expanded={archivedShown}
                  className="-ml-1.5 flex h-8 items-center gap-1.5 rounded-md px-1.5 text-table font-semibold transition-colors hover:bg-muted"
                >
                  <ChevronRight
                    size={14}
                    aria-hidden
                    className={cn('text-muted-foreground transition-transform', archivedShown && 'rotate-90')}
                  />
                  보관된 앱
                  <span className="tabular-nums text-muted-foreground">{archived.length}</span>
                  <span className="text-meta font-normal text-muted-foreground">{archivedShown ? '접기' : '펼치기'}</span>
                </button>
                {archivedShown && (
                  <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 2xl:grid-cols-3">
                    {archived.map((r) => (
                      <div key={r.app.id} className="flex min-w-0">
                        {card(r)}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
      <NewAppDialog open={creating} onClose={() => setCreating(false)} />
    </>
  )
}
